import React, { useState, useContext } from 'react';
import { Form, Button, Card, Alert, Row, Col, Accordion, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaKey } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

const LICENSE_OPTIONS = [
  { value: 'TRIAL', label: 'Trial', days: 30, description: '30 Days - For evaluation' },
  { value: 'STANDARD', label: 'Standard', days: 90, description: '90 Days - Quarterly' },
  { value: 'HALFYEAR', label: 'Half Year', days: 180, description: '180 Days - 6 Months' },
  { value: 'YEARLY', label: 'Full Year', days: 365, description: '365 Days - Annual' }
];

const CreateCustomer = () => {
  const [formData, setFormData] = useState({
    // Basic Company Info
    companyName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    
    // Company Profile (for documents)
    companySlogan: '',
    ownerName: '',
    companyPhone: '',
    companyEmail: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZipCode: '',
    
    // Contractor License Numbers (for documents)
    licenseNumber1: '',
    licenseNumber2: '',
    licenseNumber3: '',
    
    // Software License
    softwareLicenseType: 'TRIAL',
    
    // Admin User
    adminUsername: '',
    adminPassword: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const getSelectedLicense = () => {
    return LICENSE_OPTIONS.find(opt => opt.value === formData.softwareLicenseType);
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    const { companyName, address, city, state, zipCode, phone, email,
            adminUsername, adminPassword, adminEmail, adminFirstName, adminLastName } = formData;
    
    if (!companyName || !address || !city || !state || !zipCode || !phone || !email ||
        !adminUsername || !adminPassword || !adminEmail || !adminFirstName || !adminLastName) {
      setError('Please fill in all required fields');
      return;
    }
    
    // Validate password length
    if (adminPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch('/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.msg || 'Failed to create customer');
      }
      
      const data = await response.json();
      
      toast.success(`Customer created successfully with ${getSelectedLicense().label} license!`);
      navigate('/admin/customers');
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(err.message || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="form-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Add New Customer</h2>
      </div>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={onSubmit}>
            {/* Basic Company Information */}
            <h5 className="mb-3">Company Information *</h5>
            
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={onChange}
                    required
                    placeholder="Enter company name"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Owner Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={onChange}
                    placeholder="e.g., John Smith"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Company Slogan</Form.Label>
              <Form.Control
                type="text"
                name="companySlogan"
                value={formData.companySlogan}
                onChange={onChange}
                placeholder="e.g., Quality Work, Every Time"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={onChange}
                    required
                    placeholder="Enter company email"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone *</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={onChange}
                    required
                    placeholder="Enter company phone"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Address *</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={onChange}
                required
                placeholder="Enter company address"
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City *</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={onChange}
                    required
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>State *</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={onChange}
                    required
                    placeholder="State"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Zip Code *</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={onChange}
                    required
                    placeholder="Zip"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Software License */}
            <Card className="mb-4 border-primary">
              <Card.Header className="bg-primary text-white">
                <FaKey className="me-2" />
                Software License
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>License Type *</Form.Label>
                  <Form.Select
                    name="softwareLicenseType"
                    value={formData.softwareLicenseType}
                    onChange={onChange}
                    required
                  >
                    {LICENSE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label} - {option.days} Days
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                <div className="bg-light p-3 rounded">
                  <Row>
                    <Col>
                      <strong>Selected License:</strong>{' '}
                      <Badge bg={
                        formData.softwareLicenseType === 'TRIAL' ? 'info' :
                        formData.softwareLicenseType === 'STANDARD' ? 'success' :
                        formData.softwareLicenseType === 'HALFYEAR' ? 'primary' : 'warning'
                      }>
                        {getSelectedLicense()?.label}
                      </Badge>
                    </Col>
                    <Col>
                      <strong>Duration:</strong> {getSelectedLicense()?.days} days
                    </Col>
                    <Col>
                      <strong>Expires:</strong>{' '}
                      {new Date(Date.now() + getSelectedLicense()?.days * 24 * 60 * 60 * 1000).toLocaleDateString()}
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>

            {/* Contractor License Information */}
            <Accordion className="mb-4">
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <span className="fw-bold">Contractor License Numbers (Optional)</span>
                </Accordion.Header>
                <Accordion.Body>
                  <p className="text-muted small mb-3">
                    Add contractor license numbers that will appear on proposals and invoices.
                  </p>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>License #1</Form.Label>
                        <Form.Control
                          type="text"
                          name="licenseNumber1"
                          value={formData.licenseNumber1}
                          onChange={onChange}
                          placeholder="e.g., CS-010214"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>License #2</Form.Label>
                        <Form.Control
                          type="text"
                          name="licenseNumber2"
                          value={formData.licenseNumber2}
                          onChange={onChange}
                          placeholder="e.g., HIC-204662"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>License #3</Form.Label>
                        <Form.Control
                          type="text"
                          name="licenseNumber3"
                          value={formData.licenseNumber3}
                          onChange={onChange}
                          placeholder="e.g., INS-123456"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <span className="fw-bold">Document Profile Override (Optional)</span>
                </Accordion.Header>
                <Accordion.Body>
                  <p className="text-muted small mb-3">
                    If the company uses different contact info on proposals/invoices than their main contact info, enter it here. Leave blank to use the main company info above.
                  </p>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Document Phone</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyPhone"
                          value={formData.companyPhone}
                          onChange={onChange}
                          placeholder="Leave blank to use main phone"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Document Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="companyEmail"
                          value={formData.companyEmail}
                          onChange={onChange}
                          placeholder="Leave blank to use main email"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Document Address</Form.Label>
                    <Form.Control
                      type="text"
                      name="companyAddress"
                      value={formData.companyAddress}
                      onChange={onChange}
                      placeholder="Leave blank to use main address"
                    />
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyCity"
                          value={formData.companyCity}
                          onChange={onChange}
                          placeholder="City"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyState"
                          value={formData.companyState}
                          onChange={onChange}
                          placeholder="State"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Zip Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyZipCode"
                          value={formData.companyZipCode}
                          onChange={onChange}
                          placeholder="Zip"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            
            <hr className="my-4" />
            
            {/* Admin User Information */}
            <h5 className="mb-3">Admin User Information *</h5>
            <p className="text-muted mb-3">
              This will create an admin user for this customer that can access the system.
            </p>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="adminFirstName"
                    value={formData.adminFirstName}
                    onChange={onChange}
                    required
                    placeholder="Enter admin's first name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="adminLastName"
                    value={formData.adminLastName}
                    onChange={onChange}
                    required
                    placeholder="Enter admin's last name"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Username *</Form.Label>
              <Form.Control
                type="text"
                name="adminUsername"
                value={formData.adminUsername}
                onChange={onChange}
                required
                placeholder="Enter admin username"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                name="adminEmail"
                value={formData.adminEmail}
                onChange={onChange}
                required
                placeholder="Enter admin email"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Password *</Form.Label>
              <Form.Control
                type="password"
                name="adminPassword"
                value={formData.adminPassword}
                onChange={onChange}
                required
                placeholder="Enter admin password"
                minLength="6"
              />
              <Form.Text className="text-muted">
                Password must be at least 6 characters. User will be prompted to change it on first login.
              </Form.Text>
            </Form.Group>
            
            <div className="d-flex justify-content-between">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/admin/customers')}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Customer'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CreateCustomer;
